import { useState, useEffect } from 'react'

const API = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}`

const CUPOM_INICIAL = {
    codigo: '',
    tipo: 'desconto_percentual',
    valor: '',
    validade: '',
    limiteUso: 0
}

function AdminCupons() {
    const [cupons, setCupons] = useState([])
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState('')
    const [modalAberto, setModalAberto] = useState(false)
    const [cupomEditando, setCupomEditando] = useState(null)
    const [form, setForm] = useState(CUPOM_INICIAL)
    const [salvando, setSalvando] = useState(false)

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
    }

    useEffect(() => {
        carregarCupons()
    }, [])

    const carregarCupons = async () => {
        setLoading(true)
        setErro('')
        try {
            const res = await fetch(`${API}/api/admin/cupons`, { headers })
            if (!res.ok) throw new Error('Erro ao carregar cupons')
            const data = await res.json()
            setCupons(data.cupons || data || [])
        } catch (error) {
            console.error('Erro:', error)
            setErro('Não foi possível carregar os cupons.')
        } finally {
            setLoading(false)
        }
    }

    const abrirModalCriar = () => {
        setCupomEditando(null)
        setForm(CUPOM_INICIAL)
        setModalAberto(true)
    }

    const abrirModalEditar = (cupom) => {
        setCupomEditando(cupom)
        setForm({
            codigo: cupom.codigo,
            tipo: cupom.tipo,
            valor: cupom.valor,
            validade: cupom.validade ? cupom.validade.slice(0, 10) : '',
            limiteUso: cupom.limiteUso || cupom.limite_uso || 0
        })
        setModalAberto(true)
    }

    const fecharModal = () => {
        setModalAberto(false)
        setCupomEditando(null)
        setForm(CUPOM_INICIAL)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.codigo || !form.valor) {
            alert('Preencha todos os campos obrigatórios.')
            return
        }

        setSalvando(true)
        try {
            const url = cupomEditando
                ? `${API}/api/admin/cupons/${cupomEditando.id}`
                : `${API}/api/admin/cupons`
            const method = cupomEditando ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify({
                    codigo: form.codigo.toUpperCase(),
                    tipo: form.tipo,
                    valor: Number(form.valor),
                    validade: form.validade || null,
                    limiteUso: Number(form.limiteUso)
                })
            })

            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.erro || 'Erro ao salvar cupom')
            }

            fecharModal()
            carregarCupons()
        } catch (error) {
            alert('Erro: ' + error.message)
        } finally {
            setSalvando(false)
        }
    }

    const handleDesativar = async (id) => {
        if (!confirm('Deseja desativar este cupom?')) return
        try {
            const res = await fetch(`${API}/api/admin/cupons/${id}`, {
                method: 'DELETE',
                headers
            })
            if (!res.ok) throw new Error('Erro ao desativar cupom')
            carregarCupons()
        } catch (error) {
            alert('Erro: ' + error.message)
        }
    }

    const formatDate = (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '—'

    const getTipoLabel = (tipo) => {
        if (tipo === 'desconto_percentual') return 'Desconto %'
        if (tipo === 'credito_gratis') return 'Crédito Grátis'
        return tipo
    }

    const getStatusCupom = (cupom) => {
        if (cupom.status === 'inativo' || cupom.ativo === false) {
            return <span className="admin-badge admin-badge-inativo">Inativo</span>
        }
        if (cupom.validade && new Date(cupom.validade) < new Date()) {
            return <span className="admin-badge admin-badge-expirado">Expirado</span>
        }
        return <span className="admin-badge admin-badge-ativo">Ativo</span>
    }

    return (
        <div>
            <div className="admin-section-header" style={{ marginBottom: '1.5rem' }}>
                <h3>🎟️ Gestão de Cupons</h3>
                <button className="admin-btn admin-btn-primary" onClick={abrirModalCriar}>
                    + Criar Cupom
                </button>
            </div>

            {erro && <div className="admin-error">{erro}</div>}

            {loading ? (
                <div className="admin-loading">
                    <div className="spinner"></div>
                    <span>Carregando cupons...</span>
                </div>
            ) : (
                <div className="admin-section">
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Tipo</th>
                                    <th>Valor</th>
                                    <th>Usos</th>
                                    <th>Validade</th>
                                    <th>Status</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cupons.length > 0 ? (
                                    cupons.map(cupom => (
                                        <tr key={cupom.id}>
                                            <td data-label="Código" style={{ fontFamily: 'monospace', fontWeight: 700, color: '#f8fafc', letterSpacing: '0.5px' }}>
                                                {cupom.codigo}
                                            </td>
                                            <td data-label="Tipo">{getTipoLabel(cupom.tipo)}</td>
                                            <td data-label="Valor" style={{ fontWeight: 600, color: 'var(--color-primary-light)' }}>
                                                {cupom.tipo === 'desconto_percentual' ? `${cupom.valor}%` : cupom.valor}
                                            </td>
                                            <td data-label="Usos">
                                                {cupom.usosAtuais || cupom.usos_atuais || 0}
                                                {' / '}
                                                {(cupom.limiteUso || cupom.limite_uso || 0) === 0 ? '∞' : (cupom.limiteUso || cupom.limite_uso)}
                                            </td>
                                            <td data-label="Validade">{formatDate(cupom.validade)}</td>
                                            <td data-label="Status">{getStatusCupom(cupom)}</td>
                                            <td data-label="Ações">
                                                <div className="admin-actions-cell">
                                                    <button
                                                        className="admin-action-btn info"
                                                        onClick={() => abrirModalEditar(cupom)}
                                                        title="Editar"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="admin-action-btn danger"
                                                        onClick={() => handleDesativar(cupom.id)}
                                                        title="Desativar"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                            Nenhum cupom cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modalAberto && (
                <div className="admin-modal-overlay" onClick={fecharModal}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{cupomEditando ? '✏️ Editar Cupom' : '🎟️ Criar Novo Cupom'}</h3>

                        <form onSubmit={handleSubmit}>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Código</label>
                                <input
                                    type="text"
                                    className="admin-form-input"
                                    value={form.codigo}
                                    onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                                    placeholder="Ex: PROMO50"
                                    required
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Tipo</label>
                                <select
                                    className="admin-form-select"
                                    value={form.tipo}
                                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                                >
                                    <option value="desconto_percentual">Desconto Percentual</option>
                                    <option value="credito_gratis">Crédito Grátis</option>
                                </select>
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">
                                    Valor {form.tipo === 'desconto_percentual' ? '(%)' : '(créditos)'}
                                </label>
                                <input
                                    type="number"
                                    className="admin-form-input"
                                    value={form.valor}
                                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                                    placeholder="Ex: 10"
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Validade</label>
                                <input
                                    type="date"
                                    className="admin-form-input"
                                    value={form.validade}
                                    onChange={(e) => setForm({ ...form, validade: e.target.value })}
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Limite de Uso (0 = ilimitado)</label>
                                <input
                                    type="number"
                                    className="admin-form-input"
                                    value={form.limiteUso}
                                    onChange={(e) => setForm({ ...form, limiteUso: e.target.value })}
                                    min="0"
                                />
                            </div>

                            <div className="admin-modal-actions">
                                <button type="button" className="admin-btn admin-btn-secondary" onClick={fecharModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="admin-btn admin-btn-primary" disabled={salvando}>
                                    {salvando ? 'Salvando...' : (cupomEditando ? 'Salvar Alterações' : 'Criar Cupom')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminCupons
